import { TelemetryProvider } from "@lala/telemetry-sdk/web/react";
import type { AnyRouter } from "@tanstack/react-router";
import { useEffect, useRef, useState, type ReactNode } from "react";

import { PERMISSIONS } from "../lib/permissions.ts";
import { analytics } from "../lib/telemetry/analytics.ts";
import { getTelemetry } from "../lib/telemetry/client.ts";
import {
  APP_VERSION,
  TELEMETRY_APP,
  TELEMETRY_ENV,
} from "../lib/telemetry/config.ts";

import { useSession } from "./session.tsx";
import { modeOf } from "./workspace.ts";

/**
 * El patrón de la ruta en la que se está: `/work/lotes/$batchId`, no
 * `/work/lotes/7?c=…`. Es lo que se agrupa en el análisis, y así ni el id de
 * la conversación ni el mes viajan en `context.page`.
 */
export const routePatternOf = (router: AnyRouter): string | null => {
  const leaf = router.state.matches.at(-1);
  if (leaf === undefined) return null;
  // Las rutas sin segmento (`/_authenticated/chat/`) acaban en barra.
  const path = leaf.fullPath.replace(/(.)\/$/, "$1");
  return path === "" ? "/" : path;
};

/**
 * Entre la aplicación y el SDK: quién es, lo que vale para todos los eventos
 * y en qué pantalla está.
 *
 * - `identify`: el usuario de `/me` por su `userId`, o anónimo.
 * - `setBaseProperties`: app, entorno, versión, quién es --userId, correo,
 *   usuario, nombre, roles y permisos (#5)--, nivel de acceso (`manage` o
 *   `view`) y modo (`chat` o `work`).
 * - `page_view` con el PATRÓN de la ruta en cada cambio de ruta --también al
 *   cambiar de modo--, y el del arranque. El SDK cierra la anterior con su
 *   `page_leave`.
 * - Los hitos de la sesión que no son de ninguna pantalla: lista para usarse,
 *   caducada, sin acceso.
 *
 * Va dentro de `SessionProvider`. Sin telemetría configurada no hace nada.
 */
export function TelemetryBridge({
  router,
  children,
}: {
  readonly router: AnyRouter;
  readonly children: ReactNode;
}) {
  const [telemetry] = useState(getTelemetry);
  const { state, me, can } = useSession();

  // Quién es.
  const userId = me?.userId;
  useEffect(() => {
    if (telemetry === null) return;
    try {
      telemetry.identify(
        userId === undefined
          ? { type: "anonymous", id: null }
          : { type: "user", id: userId },
      );
    } catch {
      // El SDK no rompe la aplicación.
    }
  }, [telemetry, userId]);

  // Lo que va en todos los eventos.
  // Sin nadie dentro se vacían: el SDK mezcla, no reemplaza, y sin esto
  // seguirían pegados los datos de quien se fue.
  const manages = can(PERMISSIONS.manage);
  useEffect(() => {
    if (telemetry === null) return;
    try {
      telemetry.setBaseProperties({
        app: TELEMETRY_APP,
        env: TELEMETRY_ENV,
        appVersion: APP_VERSION,
        userId: me?.userId ?? null,
        email: me?.email ?? null,
        username: me?.username ?? null,
        fullName: me?.fullName ?? null,
        roles: me?.roles ?? [],
        permissions: me?.permissions ?? [],
        accessLevel: manages ? "manage" : "view",
      });
    } catch {
      // Ídem.
    }
  }, [telemetry, me, manages]);

  // La pantalla: la del arranque y cada cambio de ruta.
  useEffect(() => {
    if (telemetry === null) return undefined;
    let last: string | null = null;

    const view = () => {
      try {
        const pattern = routePatternOf(router);
        if (pattern === null || pattern === last) return;
        last = pattern;
        telemetry.setBaseProperties({
          mode: modeOf(router.state.location.pathname),
        });
        telemetry.pageview(pattern);
      } catch {
        // Ídem.
      }
    };

    if (router.state.resolvedLocation !== undefined) view();
    return router.subscribe("onResolved", view);
  }, [telemetry, router]);

  // Los hitos de la sesión.
  const previous = useRef(state.status);
  const denied = state.status === "authenticated" && !can(PERMISSIONS.access);
  useEffect(() => {
    const before = previous.current;
    previous.current = state.status;
    if (state.status === "authenticated" && !denied) analytics.app.ready();
    if (state.status === "expired" && before === "authenticated") {
      analytics.auth.sessionExpired();
    }
  }, [state.status, denied]);

  useEffect(() => {
    if (denied) analytics.auth.accessDenied();
  }, [denied]);

  if (telemetry === null) return children;
  return <TelemetryProvider client={telemetry}>{children}</TelemetryProvider>;
}
